import axios from 'axios'
import { Message } from 'element-ui'
import store from '@/store'

/**
 * 编辑锁管理工具类
 */
class EditLockManager {
  constructor() {
    this.lockInfo = null
    this.renewTimer = null
    this.timeoutSeconds = 300 // 默认5分钟
  }

  // 设置超时时间
  setTimeoutSeconds(seconds) {
    this.timeoutSeconds = seconds
  }

  // 获取超时时间
  getTimeoutSeconds() {
    return this.timeoutSeconds
  }

  // 获取续期阈值时间（超时时间的1/3，向下取整）
  getRenewThreshold() {
    return Math.floor(this.timeoutSeconds / 3)
  }

  /**
   * 获取用户名
   * @returns {string} 用户名
   */
  getUsername() {
    return store.getters.username
  }

  /**
   * 获取token
   * @returns {string} token
   */
  getToken() {
    return store.getters.token
  }

  /**
   * 获取接口路径
   * @param {string} key - 路径键
   * @returns {string} 完整接口路径
   */
  getPath(key) {
    // 例如 key: 'edit_lock/lock'
    // store.getters.path.edit_lock_lock
    // 兼容下划线和斜杠
    const pathKey = key.replace(/[\/-]/g, '_')
    return store.getters.path[pathKey] || ''
  }

  /**
   * 检查响应是否成功
   * @param {Object} response - axios 响应对象
   * @returns {boolean} 是否成功
   */
  isResponseSuccess(response) {
    const data = response.data || response
    // 支持 code 或 status 字段
    const successCode = data.code === 0  || data.status === 0 
    return successCode
  }

  /**
   * 获取响应消息
   * @param {Object} response - axios 响应对象
   * @returns {string} 消息
   */
  getResponseMessage(response) {
    const data = response.data || response
    // 支持 msg 或 message 字段
    return data.msg || data.message || '未知错误'
  }

  /**
   * 加锁方法
   * @param {string} resourceType - 资源类型
   * @param {string} resourceId - 资源ID
   * @param {boolean} enableRenew - 是否启用锁续期
   * @param {number} [timeoutSeconds] - 锁超时时间（秒），不传则使用默认
   * @returns {Promise<boolean>} - 返回是否加锁成功
   */
  async lock(resourceType, resourceId, enableRenew = false, timeoutSeconds = null) {
    try {
      // 如果传入了timeoutSeconds，更新配置
      if (timeoutSeconds !== null) {
        this.setTimeoutSeconds(timeoutSeconds)
      }

      const response = await axios.post(
        this.getPath('edit_lock/lock'),
        {
          resourceType,
          resourceId,
          timeoutSeconds: this.getTimeoutSeconds()
        },
        {
          params: {
            username: this.getUsername(),
            token: this.getToken(),
          }
        }
      )
      
      if (!this.isResponseSuccess(response)) {
        const errorMsg = this.getResponseMessage(response)
        Message.error(`加锁失败: ${errorMsg}`)
        return false
      }

      this.lockInfo = response.data.data || response.data
      if (enableRenew) {
        this.startRenewTimer(resourceType, resourceId)
      }
      return true
    } catch (error) {
      console.error('加锁请求失败:', error)
      Message.error('加锁请求失败，请检查网络连接')
      return false
    }
  }

  /**
   * 解锁方法
   * @param {string} resourceType - 资源类型
   * @param {string} resourceId - 资源ID
   * @returns {Promise<boolean>} - 返回是否解锁成功
   */
  async unlock(resourceType, resourceId) {
    try {
      this.clearRenewTimer()
      const response = await axios.post(
        this.getPath('edit_lock/unlock'),
        {
          resourceType,
          resourceId,
        },
        {
          params: {
            username: this.getUsername(),
            token: this.getToken(),
          }
        }
      )
      
      if (!this.isResponseSuccess(response)) {
        const errorMsg = this.getResponseMessage(response)
        console.warn(`解锁失败: ${errorMsg}`)
        return false
      }

      this.lockInfo = null
      return true
    } catch (error) {
      console.error('解锁请求失败:', error)
      return false
    }
  }

  /**
   * 锁续期方法
   * @param {string} resourceType - 资源类型
   * @param {string} resourceId - 资源ID
   * @returns {Promise<boolean>} - 返回是否续期成功
   */
  async renew(resourceType, resourceId) {
    try {
      const response = await axios.post(
        this.getPath('edit_lock/renew'),
        {
          resourceType,
          resourceId,
        },
        {
          params: {
            username: this.getUsername(),
            token: this.getToken(),
          }
        }
      )
      
      if (!this.isResponseSuccess(response)) {
        const errorMsg = this.getResponseMessage(response)
        console.warn(`锁续期失败: ${errorMsg}`)
        return false
      }

      this.lockInfo = response.data.data || response.data
      return true
    } catch (error) {
      console.error('锁续期请求失败:', error)
      return false
    }
  }

  /**
   * 启动锁续期定时器
   * @param {string} resourceType - 资源类型
   * @param {string} resourceId - 资源ID
   */
  startRenewTimer(resourceType, resourceId) {
    this.clearRenewTimer()

    this.renewTimer = setInterval(async () => {
      if (!this.lockInfo) return

      const now = Math.floor(Date.now() / 1000)
      const timeUntilExpire = this.lockInfo.expireTime - now
      const renewThreshold = this.getRenewThreshold()

      // 如果距离过期时间小于等于续期阈值，尝试续期
      if (timeUntilExpire <= renewThreshold) {
        const renewSuccess = await this.renew(resourceType, resourceId)

        // 如果续期失败，直接尝试重新加锁
        if (!renewSuccess) {
          console.warn(`锁续期失败，直接尝试重新加锁（距离过期${timeUntilExpire}秒，续期阈值${renewThreshold}秒）`)
          const lockSuccess = await this.lock(resourceType, resourceId, true)
          if (!lockSuccess) {
            Message.error('锁续期失败且重新加锁失败，请刷新页面重试')
            this.clearRenewTimer()
          }
        } else {
          console.log(`锁续期成功，新的过期时间: ${this.lockInfo.expireTime}`)
        }
      }
    }, 30000) // 每30秒检查一次
  }

  /**
   * 清除续期定时器
   */
  clearRenewTimer() {
    if (this.renewTimer) {
      clearInterval(this.renewTimer)
      this.renewTimer = null
    }
  }

  /**
   * 获取当前锁信息
   * @returns {Object|null} 锁信息
   */
  getLockInfo() {
    return this.lockInfo
  }

  /**
   * 检查是否持有锁
   * @returns {boolean} 是否持有锁
   */
  hasLock() {
    return this.lockInfo !== null
  }

  /**
   * 检查锁是否即将过期（距离过期时间小于等于续期阈值）
   * @returns {boolean} 是否即将过期
   */
  isExpiringSoon() {
    if (!this.lockInfo) return false

    const now = Math.floor(Date.now() / 1000)
    const timeUntilExpire = this.lockInfo.expireTime - now
    const renewThreshold = this.getRenewThreshold()

    return timeUntilExpire <= renewThreshold
  }

  // 获取距离过期的剩余时间（秒）
  getTimeUntilExpire() {
    if (!this.lockInfo) return 0
    const now = Math.floor(Date.now() / 1000)
    return Math.max(0, this.lockInfo.expireTime - now)
  }

  // 获取续期阈值时间（秒）
  getRenewThresholdSeconds() {
    return this.getRenewThreshold()
  }
}

// 创建单例实例
const editLockManager = new EditLockManager()

export default editLockManager

// 导出便捷方法
export const lock = (...args) => editLockManager.lock(...args)

export const unlock = (...args) => editLockManager.unlock(...args)

export const renew = (...args) => editLockManager.renew(...args)

export const hasLock = () => editLockManager.hasLock()

export const getLockInfo = () => editLockManager.getLockInfo()

export const isExpiringSoon = () => editLockManager.isExpiringSoon()

export const setTimeoutSeconds = (seconds) => editLockManager.setTimeoutSeconds(seconds)

export const getTimeoutSeconds = () => editLockManager.getTimeoutSeconds()

export const getRenewThresholdSeconds = () => editLockManager.getRenewThresholdSeconds()

export const getTimeUntilExpire = () => editLockManager.getTimeUntilExpire()
